import React, { useMemo } from 'react';
import styles from './RegistrationInsights.module.css';

/**
 * RegistrationInsights - Phase 3G
 * 
 * Analytics and insights for registration with:
 * - Overview cards (total, processing time, completion rate, capacity)
 * - Status breakdown chart
 * - Document verification status
 * - Timeline chart
 * - Recommendations
 */
const RegistrationInsights = ({ 
  programId, 
  applications = [], 
  config 
}) => {
  // Calculate analytics
  const analytics = useMemo(() => {
    if (!applications || applications.length === 0) {
      return {
        total: 0,
        byStatus: {},
        completionRate: 0,
        averageTime: 0,
        documentStats: {},
        timeline: []
      };
    }

    // Status breakdown
    const byStatus = applications.reduce((acc, app) => {
      acc[app.status] = (acc[app.status] || 0) + 1;
      return acc;
    }, {});

    // Completion rate (approved + rejected / total)
    const completedApps = applications.filter(app => 
      ['approved', 'rejected'].includes(app.status)
    ).length;
    const completionRate = applications.length > 0 
      ? (completedApps / applications.length) * 100 
      : 0;

    // Average processing time
    const processedApps = applications.filter(app => 
      app.submittedAt && app.reviewedAt
    );
    const averageTime = processedApps.length > 0 
      ? processedApps.reduce((acc, app) => {
          const submitted = new Date(app.submittedAt);
          const reviewed = new Date(app.reviewedAt);
          return acc + (reviewed - submitted);
        }, 0) / processedApps.length / (1000 * 60 * 60 * 24)
      : 0;

    // Document statistics
    const documentStats = applications.reduce((acc, app) => {
      if (app.documents) {
        app.documents.forEach(doc => {
          if (!acc[doc.requirementId]) {
            acc[doc.requirementId] = { submitted: 0, verified: 0, pending: 0 };
          }
          if (doc.files && doc.files.length > 0) {
            acc[doc.requirementId].submitted++;
            if (doc.files.some(f => f.status === 'verified')) {
              acc[doc.requirementId].verified++;
            } else {
              acc[doc.requirementId].pending++;
            }
          }
        });
      }
      return acc;
    }, {});

    // Timeline data (group by week)
    const timeline = applications.reduce((acc, app) => {
      const date = new Date(app.submittedAt || app.createdAt);
      const weekStart = new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      acc[weekKey] = (acc[weekKey] || 0) + 1;
      return acc;
    }, {});

    return {
      total: applications.length,
      byStatus,
      completionRate,
      averageTime,
      documentStats,
      timeline: Object.entries(timeline)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(-8)
    };
  }, [applications]);

  // Status configuration
  const STATUS_CONFIG = {
    submitted: { label: 'Submitted', color: '#3b82f6' },
    under_review: { label: 'Under Review', color: '#f59e0b' },
    documents_pending: { label: 'Docs Pending', color: '#eab308' },
    approved: { label: 'Approved', color: '#22c55e' },
    rejected: { label: 'Rejected', color: '#ef4444' }
  };

  // Format document name
  const formatDocName = (id) => {
    return id.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim().toUpperCase();
  };

  // Get insights
  const insights = useMemo(() => {
    const result = [];

    if (analytics.byStatus.documents_pending > 0) {
      result.push({
        type: 'warning',
        icon: '📎',
        title: 'Document Follow-up Needed',
        message: `${analytics.byStatus.documents_pending} applications are waiting for documents. Consider sending reminders.`
      });
    }

    if (analytics.byStatus.under_review > 5) {
      result.push({
        type: 'warning',
        icon: '⏰',
        title: 'Review Backlog',
        message: `${analytics.byStatus.under_review} applications pending review. Process them to maintain good experience.`
      });
    }

    if (config?.capacity?.maxRegistrations) {
      const approved = analytics.byStatus.approved || 0;
      const percentUsed = (approved / config.capacity.maxRegistrations) * 100;
      if (percentUsed > 80) {
        result.push({
          type: 'info',
          icon: '🎯',
          title: 'Near Capacity',
          message: `Program is at ${percentUsed.toFixed(1)}% capacity. Consider enabling waitlist or extending capacity.`
        });
      }
    }

    if (result.length === 0) {
      result.push({
        type: 'success',
        icon: '📈',
        title: 'Good Progress',
        message: 'Registration process is running smoothly. Keep monitoring for any issues.'
      });
    }

    return result;
  }, [analytics, config]);

  if (analytics.total === 0) {
    return (
      <div className={styles.registrationInsights}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📊</div>
          <h3>No Analytics Data</h3>
          <p>Analytics will appear once users start submitting registration applications.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.registrationInsights}>
      {/* Overview Cards */}
      <div className={styles.overviewCards}>
        <div className={styles.card}>
          <div className={styles.cardIcon}>📝</div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>Total Applications</span>
            <span className={styles.cardValue}>{analytics.total}</span>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardIcon}>⏱️</div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>Avg. Processing Time</span>
            <span className={styles.cardValue}>
              {analytics.averageTime > 0 ? `${analytics.averageTime.toFixed(1)} days` : 'N/A'}
            </span>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardIcon}>✅</div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>Completion Rate</span>
            <span className={styles.cardValue}>{analytics.completionRate.toFixed(1)}%</span>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardIcon}>👥</div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>Capacity Used</span>
            <span className={styles.cardValue}>
              {config?.capacity?.maxRegistrations 
                ? `${(((analytics.byStatus.approved || 0) / config.capacity.maxRegistrations) * 100).toFixed(1)}%`
                : 'Unlimited'
              }
            </span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className={styles.chartsRow}>
        {/* Status Breakdown */}
        <div className={styles.chartSection}>
          <h3>Application Status Breakdown</h3>
          <div className={styles.statusChart}>
            {Object.entries(analytics.byStatus).map(([status, count]) => {
              const config = STATUS_CONFIG[status] || { label: status, color: '#6b7280' };
              const percentage = (count / analytics.total) * 100;
              
              return (
                <div key={status} className={styles.statusItem}>
                  <div className={styles.statusInfo}>
                    <span 
                      className={styles.statusDot}
                      style={{ backgroundColor: config.color }}
                    ></span>
                    <span className={styles.statusLabel}>{config.label}</span>
                  </div>
                  <div className={styles.statusBar}>
                    <div 
                      className={styles.statusFill}
                      style={{ width: `${percentage}%`, backgroundColor: config.color }}
                    ></div>
                  </div>
                  <div className={styles.statusNumbers}>
                    <span className={styles.statusCount}>{count}</span>
                    <span className={styles.statusPercent}>{percentage.toFixed(1)}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Timeline */}
        <div className={styles.chartSection}>
          <h3>Registration Timeline (Last 8 Weeks)</h3>
          {analytics.timeline.length > 0 ? (
            <div className={styles.timelineChart}>
              {analytics.timeline.map((week, index) => {
                const maxCount = Math.max(...analytics.timeline.map(w => w.count));
                const height = maxCount > 0 ? (week.count / maxCount) * 100 : 0;
                const date = new Date(week.date);
                
                return (
                  <div key={index} className={styles.timelineBar}>
                    <div 
                      className={styles.bar}
                      style={{ height: `${height}%` }}
                      title={`${week.count} applications`}
                    >
                      <span className={styles.barCount}>{week.count}</span>
                    </div>
                    <span className={styles.barLabel}>
                      {isNaN(date.getTime()) ? 'Invalid' : date.toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.noData}>No timeline data available</div>
          )}
        </div>
      </div>

      {/* Document Stats */}
      {Object.keys(analytics.documentStats).length > 0 && (
        <div className={styles.section}>
          <h3>Document Verification Status</h3>
          <div className={styles.documentsChart}>
            {Object.entries(analytics.documentStats).map(([docType, stats]) => (
              <div key={docType} className={styles.documentItem}>
                <div className={styles.documentHeader}>
                  <span className={styles.documentName}>{formatDocName(docType)}</span>
                  <span className={styles.documentTotal}>{stats.submitted} submitted</span>
                </div>
                <div className={styles.documentProgress}>
                  <div className={styles.progressTrack}>
                    <div 
                      className={styles.progressVerified}
                      style={{ width: `${(stats.verified / Math.max(stats.submitted, 1)) * 100}%` }}
                    ></div>
                    <div 
                      className={styles.progressPending}
                      style={{ 
                        width: `${(stats.pending / Math.max(stats.submitted, 1)) * 100}%`,
                        marginLeft: `${(stats.verified / Math.max(stats.submitted, 1)) * 100}%`
                      }}
                    ></div>
                  </div>
                  <div className={styles.progressLabels}>
                    <span className={styles.verifiedLabel}>✅ {stats.verified} verified</span>
                    <span className={styles.pendingLabel}>⏳ {stats.pending} pending</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      <div className={styles.section}>
        <h3>Insights & Recommendations</h3>
        <div className={styles.insightsList}>
          {insights.map((insight, index) => (
            <div key={index} className={`${styles.insight} ${styles[insight.type]}`}>
              <span className={styles.insightIcon}>{insight.icon}</span>
              <div className={styles.insightContent}>
                <h4>{insight.title}</h4>
                <p>{insight.message}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RegistrationInsights;
